import React, { useState } from "react";
import Layout from "../components/Layout.jsx";
import "./Policies.css";
import { 
  FileText, 
  Download, 
  Eye,
  Users
} from "lucide-react";

const policyData = [
  {
    id: 1,
    department: "All",
    title: "Code of Conduct",
    description: "BuildINT Code of Conduct and Ethical Guidelines 2025",
    uploadedBy: "Admin",
    file: "/assets/document/BUILDINT-HR-POLICY.pdf",
    uploadDate: "2024-03-15",
    effectiveDate: "2025-01-01",
    fileSize: "2.4 MB",
    version: "v2.1",
    category: "HR",
    status: "active"
  },
  {
    id: 2,
    department: "All",
    title: "Zero Tolerance Policy",
    description: "Anti-harassment and discrimination policy",
    uploadedBy: "Admin",
    file: "/assets/document/Zero-Tolerance-Policies.pdf",
    uploadDate: "2024-03-10",
    effectiveDate: "2025-01-01",
    fileSize: "1.8 MB",
    version: "v1.3",
    category: "Compliance",
    status: "active"
  },
  {
    id: 3,
    department: "All",
    title: "Travel Policy",
    description: "Employee travel and expense guidelines",
    uploadedBy: "Admin",
    file: "/assets/document/Travel-Policy.pdf",
    uploadDate: "2024-02-28",
    effectiveDate: "2024-07-01",
    fileSize: "1.2 MB",
    version: "v3.0",
    category: "Finance",
    status: "active"
  },
  {
    id: 4,
    department: "All",
    title: "Letter of Contract",
    description: "Standard employment contract template",
    uploadedBy: "Admin",
    file: "/assets/document/Letter-of-contract.pdf",
    uploadDate: "2024-02-15",
    effectiveDate: "2024-01-01",
    fileSize: "0.9 MB",
    version: "v2.0",
    category: "HR",
    status: "active"
  },
];

function Policies() {
  const [previewFile, setPreviewFile] = useState(null);

  const handlePreview = (file) => {
    setPreviewFile(file);
    document.body.style.overflow = "hidden";
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
    document.body.style.overflow = "auto";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="page-header" style={{ marginBottom: "0.5rem" }}>
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="pagetitle">
              <h1 style={{ marginBottom: 0 }}>Policy Documents</h1>
              <small className="text-muted">All policies are effective as of Jan 1, 2025</small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-end">
              <span className="text-muted small">({policyData.length} of {policyData.length})</span>
            </div>
          </div>
        </div>
      </div>

      {/* POLICIES TABLE */}
      <div className="card" style={{ height: "calc(100vh - 160px)", display: "flex", flexDirection: "column" }}>
        <div className="table-responsive-scroll" style={{ flex: 1, overflowY: "auto" }}>
          <table className="table table-hover policy-table mb-0">
            <thead>
              <tr>
                <th>POLICY</th>
                <th>DESCRIPTION</th>
                <th>CATEGORY</th>
                <th>EFFECTIVE DATE</th>
                <th>VERSION</th>
                <th className="text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {policyData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <FileText size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">No policies found</h5>
                  </td>
                </tr>
              ) : (
                policyData.map((policy) => (
                  <tr key={policy.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="policy-icon">
                          <FileText size={20} />
                        </div>
                        <div className="ms-3">
                          <strong className="d-block">{policy.title}</strong>
                          <small className="text-muted">
                            <Users size={12} className="me-1" />
                            Uploaded by {policy.uploadedBy} • {policy.fileSize}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="policy-description">
                        {policy.description}
                      </div>
                    </td>
                    <td>
                      <span className={`badge category-badge category-${policy.category.toLowerCase()}`}>
                        {policy.category}
                      </span>
                    </td>
                    <td>
                      <div className="policy-date">
                        {formatDate(policy.effectiveDate)}
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark version-badge">
                        {policy.version}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handlePreview(policy.file)}
                          title="Preview Policy"
                        >
                          <Eye size={16} />
                        </button>
                        <a
                          href={policy.file}
                          download
                          className="btn btn-sm btn-success"
                          title="Download Policy"
                        >
                          <Download size={16} />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PDF PREVIEW MODAL */}
      {previewFile && (
          <div className="policy-preview-modal">
            {/* CUSTOM CONTROLS BAR */}
            <div className="policy-preview-controls">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <FileText size={20} className="text-white" />
                  <h6 className="mb-0 text-white">Policy Preview</h6>
                </div>
                <div className="d-flex gap-2">
                  <a
                    href={previewFile}
                    download
                    className="btn btn-sm btn-light d-flex align-items-center gap-1"
                  >
                    <Download size={14} />
                    Download
                  </a>
                  <button
                    className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                    onClick={handleClosePreview}
                  >
                    <span style={{ fontSize: "18px" }}>×</span>
                    Close
                  </button>
                </div>
              </div>
            </div>

            {/* PDF VIEWER */}
            <div className="policy-preview-body">
              <object
                data={`${previewFile}#toolbar=0&navpanes=0`}
                type="application/pdf"
                width="100%"
                height="100%"
              >
                <div className="pdf-fallback">
                  <FileText size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">PDF Preview Not Available</h5>
                  <p className="text-muted">
                    Your browser doesn't support embedded PDFs.
                  </p>
                  <a href={previewFile} className="btn btn-primary">
                    <Download size={16} className="me-2" />
                    Download PDF
                  </a>
                </div>
              </object>
            </div>
          </div>
        )}
    </Layout>
  );
}

export default Policies;